/**
 * Formations, and how a shape changes between phases of play.
 *
 * Coordinates are fractions of the pitch: `x` runs across it (0 = left touchline
 * from the team's own perspective, 1 = right) and `y` runs from the team's own
 * goal line (0) towards the opponent's (1). Fractions rather than pixels so a
 * placement means the same thing on a phone, a laptop and an A4 page.
 */

export const PHASES = [
  'base',
  'in_possession',
  'out_of_possession',
  'attacking_transition',
  'defensive_transition',
] as const;

export type PlayPhase = (typeof PHASES)[number];

export interface Slot {
  /** Position code, matching the seeded position config where possible. */
  positionKey: string;
  x: number;
  y: number;
}

export interface Formation {
  key: string;
  slots: Slot[];
}

/**
 * The shapes a coach is most likely to start from. Every slot is editable
 * afterwards — these are a starting point, not a model of how to play.
 */
export const FORMATIONS: readonly Formation[] = [
  {
    key: '4-2-3-1',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LB', x: 0.12, y: 0.25 },
      { positionKey: 'LCB', x: 0.36, y: 0.19 },
      { positionKey: 'RCB', x: 0.64, y: 0.19 },
      { positionKey: 'RB', x: 0.88, y: 0.25 },
      { positionKey: 'DM', x: 0.38, y: 0.42 },
      { positionKey: 'DM', x: 0.62, y: 0.42 },
      { positionKey: 'LW', x: 0.15, y: 0.66 },
      { positionKey: 'AM', x: 0.5, y: 0.62 },
      { positionKey: 'RW', x: 0.85, y: 0.66 },
      { positionKey: 'ST', x: 0.5, y: 0.85 },
    ],
  },
  {
    key: '4-3-3',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LB', x: 0.12, y: 0.25 },
      { positionKey: 'LCB', x: 0.36, y: 0.19 },
      { positionKey: 'RCB', x: 0.64, y: 0.19 },
      { positionKey: 'RB', x: 0.88, y: 0.25 },
      { positionKey: 'DM', x: 0.5, y: 0.4 },
      { positionKey: 'CM', x: 0.3, y: 0.53 },
      { positionKey: 'CM', x: 0.7, y: 0.53 },
      { positionKey: 'LW', x: 0.15, y: 0.76 },
      { positionKey: 'ST', x: 0.5, y: 0.85 },
      { positionKey: 'RW', x: 0.85, y: 0.76 },
    ],
  },
  {
    key: '4-4-2',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LB', x: 0.12, y: 0.25 },
      { positionKey: 'LCB', x: 0.36, y: 0.19 },
      { positionKey: 'RCB', x: 0.64, y: 0.19 },
      { positionKey: 'RB', x: 0.88, y: 0.25 },
      { positionKey: 'LM', x: 0.12, y: 0.53 },
      { positionKey: 'CM', x: 0.38, y: 0.48 },
      { positionKey: 'CM', x: 0.62, y: 0.48 },
      { positionKey: 'RM', x: 0.88, y: 0.53 },
      { positionKey: 'ST', x: 0.38, y: 0.83 },
      { positionKey: 'ST', x: 0.62, y: 0.83 },
    ],
  },
  {
    key: '4-1-4-1',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LB', x: 0.12, y: 0.25 },
      { positionKey: 'LCB', x: 0.36, y: 0.19 },
      { positionKey: 'RCB', x: 0.64, y: 0.19 },
      { positionKey: 'RB', x: 0.88, y: 0.25 },
      { positionKey: 'DM', x: 0.5, y: 0.38 },
      { positionKey: 'LM', x: 0.12, y: 0.58 },
      { positionKey: 'CM', x: 0.38, y: 0.55 },
      { positionKey: 'CM', x: 0.62, y: 0.55 },
      { positionKey: 'RM', x: 0.88, y: 0.58 },
      { positionKey: 'ST', x: 0.5, y: 0.85 },
    ],
  },
  {
    key: '4-4-1-1',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LB', x: 0.12, y: 0.25 },
      { positionKey: 'LCB', x: 0.36, y: 0.19 },
      { positionKey: 'RCB', x: 0.64, y: 0.19 },
      { positionKey: 'RB', x: 0.88, y: 0.25 },
      { positionKey: 'LM', x: 0.12, y: 0.52 },
      { positionKey: 'CM', x: 0.38, y: 0.48 },
      { positionKey: 'CM', x: 0.62, y: 0.48 },
      { positionKey: 'RM', x: 0.88, y: 0.52 },
      { positionKey: 'AM', x: 0.5, y: 0.68 },
      { positionKey: 'ST', x: 0.5, y: 0.87 },
    ],
  },
  {
    key: '3-5-2',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LCB', x: 0.26, y: 0.2 },
      { positionKey: 'CB', x: 0.5, y: 0.17 },
      { positionKey: 'RCB', x: 0.74, y: 0.2 },
      { positionKey: 'LWB', x: 0.08, y: 0.5 },
      { positionKey: 'CM', x: 0.34, y: 0.45 },
      { positionKey: 'DM', x: 0.5, y: 0.4 },
      { positionKey: 'CM', x: 0.66, y: 0.45 },
      { positionKey: 'RWB', x: 0.92, y: 0.5 },
      { positionKey: 'ST', x: 0.38, y: 0.83 },
      { positionKey: 'ST', x: 0.62, y: 0.83 },
    ],
  },
  {
    key: '3-4-3',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LCB', x: 0.26, y: 0.2 },
      { positionKey: 'CB', x: 0.5, y: 0.17 },
      { positionKey: 'RCB', x: 0.74, y: 0.2 },
      { positionKey: 'LWB', x: 0.08, y: 0.5 },
      { positionKey: 'CM', x: 0.38, y: 0.45 },
      { positionKey: 'CM', x: 0.62, y: 0.45 },
      { positionKey: 'RWB', x: 0.92, y: 0.5 },
      { positionKey: 'LW', x: 0.18, y: 0.78 },
      { positionKey: 'ST', x: 0.5, y: 0.86 },
      { positionKey: 'RW', x: 0.82, y: 0.78 },
    ],
  },
  {
    key: '5-3-2',
    slots: [
      { positionKey: 'GK', x: 0.5, y: 0.05 },
      { positionKey: 'LWB', x: 0.08, y: 0.3 },
      { positionKey: 'LCB', x: 0.28, y: 0.19 },
      { positionKey: 'CB', x: 0.5, y: 0.16 },
      { positionKey: 'RCB', x: 0.72, y: 0.19 },
      { positionKey: 'RWB', x: 0.92, y: 0.3 },
      { positionKey: 'CM', x: 0.3, y: 0.5 },
      { positionKey: 'DM', x: 0.5, y: 0.45 },
      { positionKey: 'CM', x: 0.7, y: 0.5 },
      { positionKey: 'ST', x: 0.38, y: 0.82 },
      { positionKey: 'ST', x: 0.62, y: 0.82 },
    ],
  },
];

export function findFormation(key: string): Formation | undefined {
  return FORMATIONS.find((f) => f.key === key);
}

/** Which unit a slot belongs to, used for colour and for phase behaviour. */
export function unitOf(positionKey: string): 'gk' | 'defence' | 'midfield' | 'attack' {
  const key = positionKey.toUpperCase();
  if (key === 'GK') return 'gk';
  if (/^(L|R)?(C)?B$|^(L|R)WB$|^(L|R)B$/.test(key)) return 'defence';
  if (/^(D|C|A)M$|^(L|R)M$/.test(key)) return 'midfield';
  return 'attack';
}

const clamp01 = (n: number) => Math.min(0.97, Math.max(0.03, n));

/**
 * A sensible starting shape for a phase, derived from the base.
 *
 * These are the movements almost every team makes, so a coach adjusts rather
 * than rebuilds: the block drops and narrows without the ball, full backs and
 * wingers push and hold width with it, the side gets long in an attacking
 * transition and compresses towards its own goal in a defensive one.
 *
 * They are only a starting point — every placement stays editable, because the
 * product must not encode one way of playing.
 */
export function phaseShape(base: readonly Slot[], phase: PlayPhase): Slot[] {
  if (phase === 'base') return base.map((s) => ({ ...s }));

  return base.map((slot) => {
    const unit = unitOf(slot.positionKey);
    if (unit === 'gk') {
      // The keeper's only meaningful movement is starting height.
      const dy =
        phase === 'in_possession' ? 0.05 : phase === 'attacking_transition' ? 0.04 : 0;
      return { ...slot, y: clamp01(slot.y + dy) };
    }

    const centre = 0.5;
    let dy = 0;
    let narrow = 0;

    switch (phase) {
      case 'in_possession':
        // Full backs and wingers advance; width is held, not narrowed.
        dy = unit === 'defence' ? 0.12 : unit === 'midfield' ? 0.08 : 0.04;
        narrow = -0.03;
        break;
      case 'out_of_possession':
        // The block drops and narrows — the shape that actually defends.
        dy = unit === 'attack' ? -0.22 : unit === 'midfield' ? -0.12 : -0.05;
        narrow = 0.16;
        break;
      case 'attacking_transition':
        // Get long and direct: the front line runs, the rest supports behind.
        dy = unit === 'attack' ? 0.08 : unit === 'midfield' ? 0.14 : 0.06;
        narrow = 0.04;
        break;
      case 'defensive_transition':
        // Counterpress near the ball, everyone else recovers goal-side.
        dy = unit === 'attack' ? -0.12 : unit === 'midfield' ? -0.1 : -0.08;
        narrow = 0.2;
        break;
    }

    const x = clamp01(slot.x + (centre - slot.x) * narrow);
    return { ...slot, x, y: clamp01(slot.y + dy) };
  });
}

export const PLAYER_ROLES = [
  'crucial',
  'important',
  'rotation',
  'squad',
  'prospect',
  'fringe',
] as const;

export type PlayerRole = (typeof PLAYER_ROLES)[number];
